import React, { useMemo, useRef, useEffect } from 'react';
import { createPortal, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { TextureLoader, PlaneGeometry, DoubleSide } from 'three';

import leafTexture1 from './img/1.png';
import leafTexture2 from './img/2.png';
import leafTexture3 from './img/3.png';

// Configs

const LEAF_COUNT = 100;
const LEAF_SIZE = 8;
const AREA = 500;
const LIMIT = AREA / 2;
const HEIGHT_LIMIT = AREA * 0.2;

const BASE_BREEZE_MIN = 0.2;
const BASE_BREEZE_MAX = 1.8;

// Spawn
const SPAWN_OUTER = LIMIT * 0.85;
const SPAWN_INNER = LIMIT * 0.25;
const CENTER_SPAWN_CHANCE = 0.2;

// Camera
const CAMERA_NEAR_DIST = 1;
const CAMERA_NEAR_SCALE = 0.4;

// Loop
const ZONE_ON = 3.0;
const ZONE_OFF = 10.0;

// Zone
const ZONE_HALF_WIDTH = LIMIT * 100;
const ZONE_HALF_HEIGHT = LIMIT * 0.3;
const ZONE_HALF_DEPTH = LIMIT * 100;

// Orbit
const ORBIT_DURATION = 2.5;
const ORBIT_OMEGA = 4.0;
const ORBIT_OUT_SPEED = 60.0;
const ORBIT_TANGENTIAL_MAX = 140.0;
const ORBIT_RESPONSE = 8.0;

// Y offset
const Y_OFFSET = 30;

function rand(min, max) {
  return min + Math.random() * (max - min);
}

function respawnLeaf(leaf, spawnCenter) {
  const ud = leaf.userData;
  const cx = spawnCenter?.x ?? 0;
  const cy = spawnCenter?.y ?? 0;
  const cz = spawnCenter?.z ?? 0;

  const spawnY = cy + HEIGHT_LIMIT + Math.random() * HEIGHT_LIMIT * 0.7;

  if (Math.random() < CENTER_SPAWN_CHANCE) {
    const a = Math.random() * Math.PI * 2;
    const r = Math.sqrt(Math.random()) * (LIMIT * 0.22);
    leaf.position.set(cx + Math.cos(a) * r, spawnY, cz + Math.sin(a) * r);
  } else {
    const sx = (Math.random() < 0.5 ? -1 : 1) * rand(SPAWN_INNER, SPAWN_OUTER);
    const sz = (Math.random() < 0.5 ? -1 : 1) * rand(SPAWN_INNER, SPAWN_OUTER);
    leaf.position.set(cx + sx, spawnY, cz + sz);
  }

  const s = 0.6 + Math.random() * 1.0;
  ud.baseScale = s;
  leaf.scale.setScalar(s);

  leaf.rotation.set(
    Math.random() * Math.PI,
    Math.random() * Math.PI,
    Math.random() * Math.PI
  );

  if (!ud.velocity) ud.velocity = new THREE.Vector3();
  if (!ud.rotSpeed) ud.rotSpeed = new THREE.Vector3();

  ud.baseFall = 11.5 + Math.random() * 25.0;
  ud.windFactor = 0.65 + Math.random() * 0.05;
  ud.turbulence = 0.6 + Math.random() * 0.4;
  ud.flutterHz = 0.6 + Math.random() * 1.8;
  ud.phase = Math.random() * Math.PI * 2;

  ud.velocity.set(0, -ud.baseFall, 0);
  ud.rotSpeed.set(
    (Math.random() - 0.5) * 0.5,
    (Math.random() - 0.5) * 2.2,
    (Math.random() - 0.5) * 0.9
  );

  ud.leanX = 0;
  ud.leanZ = 0;
  ud.zoneHit = false;
  ud.orbitT = 0;
  ud.orbitSign = 1;
  ud.ejecting = false;
}

export default function LeafFallLayer({ visible = true }) {
  const { camera, scene } = useThree();
  const groupRef = useRef();
  const leavesRef = useRef([]);
  const timeRef = useRef(0);

  const windRef = useRef(new THREE.Vector3());
  const zoneRef = useRef({
    active: true,
    timer: ZONE_ON,
    turnSign: 1,
  });

  const tmpRef = useRef({
    invQ: new THREE.Quaternion(),
    rel: new THREE.Vector3(),
    local: new THREE.Vector3(),
    axis: new THREE.Vector3(),
    plane: new THREE.Vector3(),
    planeN: new THREE.Vector3(),
    tangent: new THREE.Vector3(),
    target: new THREE.Vector3(),
  });

  const leafTextures = useMemo(() => {
    const loader = new TextureLoader();
    return [
      loader.load(leafTexture1),
      loader.load(leafTexture2),
      loader.load(leafTexture3),
    ];
  }, []);

  const leafGeometry = useMemo(
    () => new PlaneGeometry(LEAF_SIZE, LEAF_SIZE),
    []
  );

  useEffect(() => {
    if (!groupRef.current || !leafTextures.length) return;
    const leaves = [];
    const group = groupRef.current;

    while (group.children.length > 0) {
      const child = group.children[0];
      if (child.material) child.material.dispose();
      group.remove(child);
    }

    for (let i = 0; i < LEAF_COUNT; i++) {
      const tex = leafTextures[i % leafTextures.length];
      const material = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        alphaTest: 0.05,
        side: DoubleSide,
        toneMapped: false,
      });

      const mesh = new THREE.Mesh(leafGeometry, material);
      mesh.renderOrder = 102;
      mesh.raycast = () => null;
      respawnLeaf(mesh, camera?.position);
      group.add(mesh);
      leaves.push(mesh);
    }
    leavesRef.current = leaves;

    return () => {
      leaves.forEach((leaf) => leaf.material?.dispose());
      leavesRef.current = [];
    };
  }, [leafTextures, leafGeometry, camera]);

  useEffect(() => {
    return () => leafGeometry?.dispose();
  }, [leafGeometry]);

  useFrame((_, delta) => {
    if (!visible || !leavesRef.current.length) return;

    const dt = Math.min(delta, 1 / 30);
    timeRef.current += dt;
    const t = timeRef.current; // --- Global wind ---

    const baseAngle = Math.sin(t * 0.12) * 0.9 + Math.sin(t * 0.05 + 1.7) * 0.5;
    const baseSpeed =
      BASE_BREEZE_MIN +
      (BASE_BREEZE_MAX - BASE_BREEZE_MIN) *
        (0.5 + 0.5 * Math.sin(t * 0.08 + 0.4));

    let windX = Math.cos(baseAngle) * baseSpeed;
    let windZ = Math.sin(baseAngle) * baseSpeed;
    windRef.current.set(windX, 0, windZ); // --- Zone timing & Turn randomization ---

    const zone = zoneRef.current;
    zone.timer -= dt;
    if (zone.timer <= 0) {
      zone.active = !zone.active;
      zone.timer = zone.active ? ZONE_ON : ZONE_OFF; // Khi bắt đầu turn mới, random hướng xoay duy nhất cho turn đó

      if (zone.active) {
        zone.turnSign = Math.random() < 0.5 ? -1 : 1;
      }
    }

    let zoneEnv = 0;
    if (zone.active) {
      const p = 1 - zone.timer / ZONE_ON;
      const inAmt = THREE.MathUtils.smoothstep(p, 0, 0.18);
      const outAmt = 1 - THREE.MathUtils.smoothstep(p, 0.78, 1);
      zoneEnv = inAmt * outAmt;
    }

    const leaves = leavesRef.current;
    const limit = LIMIT;
    const camPos = camera?.position;
    const wrapCx = camPos?.x ?? 0;
    const wrapCy = camPos?.y ?? 0;
    const wrapCz = camPos?.z ?? 0;
    const fallEndY = wrapCy - limit - LEAF_SIZE;
    const outLimit = limit * 1.08;

    const tmp = tmpRef.current;
    if (camera) {
      tmp.invQ.copy(camera.quaternion).invert();
      tmp.axis.set(0, 1, 0); // World Y axis
    }

    for (const leaf of leaves) {
      const ud = leaf.userData; // Distance scaling

      if (camPos && ud.baseScale) {
        const dist = leaf.position.distanceTo(camPos);
        if (dist < CAMERA_NEAR_DIST) {
          const k = THREE.MathUtils.clamp(dist / CAMERA_NEAR_DIST, 0, 1);
          const m = THREE.MathUtils.lerp(CAMERA_NEAR_SCALE, 1, k);
          leaf.scale.setScalar(ud.baseScale * m);
        } else {
          leaf.scale.setScalar(ud.baseScale);
        }
      } // Physics

      const nx =
        (Math.sin(t * 0.9 + ud.phase) + Math.sin(t * 2.1 + ud.phase * 2.2)) *
        0.5;
      const nz =
        (Math.cos(t * 0.85 + ud.phase) + Math.sin(t * 1.9 + ud.phase * 1.7)) *
        0.5;
      const targetVx = windX * ud.windFactor + nx * ud.turbulence;
      const targetVz = windZ * ud.windFactor + nz * ud.turbulence;
      const flutter = 0.5 + 0.5 * Math.sin(t * ud.flutterHz + ud.phase);
      let targetVy = -ud.baseFall * (0.86 + 0.14 * flutter); // Zone check

      let inZone = false;
      if (zoneEnv > 0 && camPos && camera) {
        tmp.rel.copy(leaf.position).sub(camPos);
        tmp.local.copy(tmp.rel).applyQuaternion(tmp.invQ);
        tmp.local.y += Y_OFFSET;
        inZone =
          Math.abs(tmp.local.x) <= ZONE_HALF_WIDTH &&
          Math.abs(tmp.local.y) <= ZONE_HALF_HEIGHT &&
          Math.abs(tmp.local.z) <= ZONE_HALF_DEPTH;
      }

      if (inZone && !ud.zoneHit) {
        ud.zoneHit = true;
        ud.orbitT = ORBIT_DURATION;
        ud.ejecting = true; // Áp dụng turnSign từ zoneRef
        ud.orbitSign = zone.turnSign;
      } else if (!inZone) {
        ud.zoneHit = false;
      }

      let spinBoost = 1.0;
      if (ud.orbitT > 0 && camPos && camera) {
        ud.orbitT = Math.max(0, ud.orbitT - dt);
        tmp.rel.copy(leaf.position).sub(camPos);
        const dot = tmp.rel.dot(tmp.axis);
        tmp.plane.copy(tmp.rel).addScaledVector(tmp.axis, -dot);
        const rLen = tmp.plane.length();

        if (rLen > 1e-5) {
          tmp.planeN.copy(tmp.plane).multiplyScalar(1 / rLen);
          tmp.tangent
            .copy(tmp.axis)
            .cross(tmp.planeN)
            .normalize()
            .multiplyScalar(ud.orbitSign);
          const tangSpeed = Math.min(ORBIT_OMEGA * rLen, ORBIT_TANGENTIAL_MAX);
          tmp.target
            .copy(tmp.tangent)
            .multiplyScalar(tangSpeed)
            .addScaledVector(tmp.planeN, ORBIT_OUT_SPEED);

          tmp.target.y = -ud.baseFall * 3;

          ud.velocity.x = THREE.MathUtils.damp(
            ud.velocity.x,
            tmp.target.x,
            ORBIT_RESPONSE,
            dt
          );
          ud.velocity.y = THREE.MathUtils.damp(
            ud.velocity.y,
            tmp.target.y,
            ORBIT_RESPONSE,
            dt
          );
          ud.velocity.z = THREE.MathUtils.damp(
            ud.velocity.z,
            tmp.target.z,
            ORBIT_RESPONSE,
            dt
          );
        }
        spinBoost = 1 + 2.0 * zoneEnv;
      } else {
        ud.velocity.x = THREE.MathUtils.damp(ud.velocity.x, targetVx, 1.8, dt);
        ud.velocity.z = THREE.MathUtils.damp(ud.velocity.z, targetVz, 1.8, dt);
        ud.velocity.y = THREE.MathUtils.damp(ud.velocity.y, targetVy, 1.2, dt);
      }

      leaf.position.addScaledVector(ud.velocity, dt); // Animation & Wrapping

      leaf.rotation.x += ud.rotSpeed.x * dt * spinBoost;
      leaf.rotation.y += ud.rotSpeed.y * dt * spinBoost;
      leaf.rotation.z += ud.rotSpeed.z * dt * spinBoost;

      const targetLeanX = THREE.MathUtils.clamp(windZ * 0.02, -0.55, 0.55);
      const targetLeanZ = THREE.MathUtils.clamp(-windX * 0.02, -0.55, 0.55);
      ud.leanX = THREE.MathUtils.damp(ud.leanX, targetLeanX, 4.0, dt);
      ud.leanZ = THREE.MathUtils.damp(ud.leanZ, targetLeanZ, 4.0, dt);
      leaf.rotation.x += ud.leanX;
      leaf.rotation.z += ud.leanZ;

      if (ud.ejecting) {
        if (leaf.position.distanceTo(camPos) > outLimit)
          respawnLeaf(leaf, camPos);
        if (ud.orbitT <= 0) ud.ejecting = true;
      } else {
        if (Math.abs(leaf.position.x - wrapCx) > limit)
          leaf.position.x =
            wrapCx - Math.sign(leaf.position.x - wrapCx) * limit;
        if (Math.abs(leaf.position.z - wrapCz) > limit)
          leaf.position.z =
            wrapCz - Math.sign(leaf.position.z - wrapCz) * limit;
        if (leaf.position.y < fallEndY) respawnLeaf(leaf, camPos);
      }
    }
  });

  return createPortal(<group ref={groupRef} visible={visible} />, scene);
}
